/**
 * Utility functions for combining multiple form instances into a single view
 * and splitting them back on submission
 * SPECIFICALLY FOR RETURNING STUDENT ENROLLMENT WORKFLOW
 * 
 * This is separate from formCombiner.js to keep returning student logic isolated
 * from new student enrollment logic.
 */

/**
 * Combines multiple form instances into a single form definition
 * Prefixes field names with form instance ID to avoid conflicts
 */
export const combineReturningStudentFormInstances = (formInstances) => {
  if (!formInstances || formInstances.length === 0) {
    return null;
  }

  const combinedSections = [];
  const formInstanceMap = {}; // Maps prefixed field names to form instance IDs

  formInstances.forEach((instance) => {
    const instanceId = instance.id;
    let formData = instance.form_data;

    // Parse form_data if it's a string
    if (typeof formData === 'string') {
      try {
        formData = JSON.parse(formData);
      } catch (e) {
        console.error(`[returningStudentFormCombiner] Failed to parse form_data for instance ${instanceId}:`, e);
        return; // Skip this instance
      }
    }

    if (!formData || !formData.sections) {
      return; // Skip if no sections
    }

    // Add form title section
    combinedSections.push({
      section_name: instance.instance_name || instance.master_form_name,
      section_note: instance.instance_description || null,
      is_form_header: true,
      form_instance_id: instanceId,
      is_required: instance.is_required || false,
      fields: []
    });

    // Process each section
    formData.sections.forEach((section, sectionIndex) => {
      // Skip admin-only sections for parent view
      if (section.admin_only) {
        return;
      }

      // Handle sections with allows_multiple (repeating groups)
      if (section.allows_multiple) {
        // Create a single repeatable_group field for the entire section
        const repeatableField = {
          name: `${instanceId}_repeating_group_${sectionIndex}`,
          label: section.section_name,
          type: 'repeatable_group',
          allows_multiple: true,
          min_items: 0,
          max_items: 10,
          form_instance_id: instanceId,
          fields: []
        };

        // Process fields within the repeating group
        section.fields.forEach((field) => {
          // Skip admin-only fields
          if (field.admin_only) {
            return;
          }

          // Apply field overrides if they exist
          const fieldOverrides = instance.field_overrides || {};
          const override = fieldOverrides[field.name];

          // Skip hidden fields
          if (override && override.is_hidden === true) {
            return;
          }

          // Determine if field is required (override takes precedence)
          const isRequired = override && override.is_required !== null && override.is_required !== undefined
            ? override.is_required
            : field.required || false;

          repeatableField.fields.push({
            ...field,
            name: field.name, // Keep original name within the group
            required: isRequired // Apply override
          });
        });

        const combinedSection = {
          section_name: section.section_name,
          section_note: section.section_note,
          form_instance_id: instanceId,
          fields: [repeatableField]
        };

        combinedSections.push(combinedSection);
        return;
      }

      const combinedSection = {
        section_name: section.section_name,
        section_note: section.section_note,
        form_instance_id: instanceId,
        fields: []
      };

      // Process fields and prefix names
      section.fields.forEach((field) => {
        // Skip admin-only fields
        if (field.admin_only) {
          return;
        }

        // Apply field overrides if they exist
        const fieldOverrides = instance.field_overrides || {};
        const override = fieldOverrides[field.name];

        // Skip hidden fields
        if (override && override.is_hidden === true) {
          return;
        }

        // Determine if field is required (override takes precedence)
        const isRequired = override && override.is_required !== null && override.is_required !== undefined
          ? override.is_required
          : field.required || false;

        const prefixedField = {
          ...field,
          name: `${instanceId}_${field.name}`,
          original_name: field.name, // Keep original for splitting
          form_instance_id: instanceId,
          required: isRequired // Apply override
        };

        // Update conditional field references
        if (prefixedField.conditional_fields) {
          // Handle conditional_fields structure
          if (prefixedField.conditional_fields.fields) {
            prefixedField.conditional_fields.fields = prefixedField.conditional_fields.fields.map(fieldName => {
              // Only prefix if it's not already prefixed (doesn't start with a UUID pattern)
              if (!fieldName.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i)) {
                return `${instanceId}_${fieldName}`;
              }
              return fieldName;
            });
          }
        }

        // Handle autofill_source field references
        if (prefixedField.autofill_source) {
          if (prefixedField.autofill_source.source_fields) {
            prefixedField.autofill_source.source_fields = prefixedField.autofill_source.source_fields.map(fieldName => {
              if (!fieldName.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i)) {
                return `${instanceId}_${fieldName}`;
              }
              return fieldName;
            });
          }
          if (prefixedField.autofill_source.target_fields) {
            prefixedField.autofill_source.target_fields = prefixedField.autofill_source.target_fields.map(fieldName => {
              if (!fieldName.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i)) {
                return `${instanceId}_${fieldName}`;
              }
              return fieldName;
            });
          }
        }

        combinedSection.fields.push(prefixedField);
        formInstanceMap[prefixedField.name] = instanceId;
      });

      combinedSections.push(combinedSection);
    });
  });

  return {
    sections: combinedSections,
    formInstanceMap,
    formInstances: formInstances.map(inst => ({
      id: inst.id,
      instance_name: inst.instance_name,
      master_form_name: inst.master_form_name,
      allows_multiple: inst.form_data?.allows_multiple || false
    }))
  };
};

/**
 * Splits combined form data back into individual form instance data
 */
export const splitReturningStudentFormDataByInstance = (combinedData, formInstanceMap, formInstances) => {
  const splitData = {};

  // Extract fields that belong to each instance
  formInstances.forEach((instance) => {
    const instanceId = instance.id;
    const instanceData = {};

    // Extract fields that belong to this instance
    Object.keys(combinedData).forEach((prefixedFieldName) => {
      if (prefixedFieldName.startsWith(`${instanceId}_`)) {
        const originalFieldName = prefixedFieldName.replace(`${instanceId}_`, '');
        instanceData[originalFieldName] = combinedData[prefixedFieldName];
      }
    });

    splitData[instanceId] = instanceData;
  });

  return splitData;
};

/**
 * Extracts student information from returning student form data
 */
export const extractReturningStudentInfo = (formData, formInstanceMap, formInstances = []) => {
  // Find the Returning Student Form instance by name
  const returningStudentFormInstance = formInstances.find(inst => 
    inst.instance_name?.toLowerCase().includes('returning student') ||
    inst.master_form_name?.toLowerCase().includes('returning student')
  );

  const targetInstanceId = returningStudentFormInstance?.id;

  // Look for student fields in the combined data
  const studentFields = ['legal_name_first', 'legal_name_last', 'legal_name_middle',
                         'grade', 'date_of_birth', 'gender', 'previous_last_name',
                         'social_security_number', 'nickname'];

  const studentInfo = {};

  // Search through all prefixed fields
  Object.keys(formData).forEach((prefixedFieldName) => {
    // Extract instance ID and original field name
    const uuidPattern = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})_(.+)$/i;
    const match = prefixedFieldName.match(uuidPattern);
    
    if (!match) {
      return; // Skip if not prefixed
    }

    const instanceId = match[1];
    const originalFieldName = match[2];
    
    // Only process fields from the Returning Student Form instance (if we found it)
    if (targetInstanceId && instanceId !== targetInstanceId) {
      return;
    }
    
    if (studentFields.includes(originalFieldName)) {
      const value = formData[prefixedFieldName];

      // Map to student table fields
      if (originalFieldName === 'legal_name_first') {
        studentInfo.first_name = value || studentInfo.first_name;
      } else if (originalFieldName === 'legal_name_last') {
        studentInfo.last_name = value || studentInfo.last_name;
      } else if (originalFieldName === 'legal_name_middle') {
        studentInfo.middle_name = value;
      } else if (originalFieldName === 'gender') {
        studentInfo.gender = value;
      } else if (originalFieldName === 'grade') {
        studentInfo.grade = value;
      } else if (originalFieldName === 'date_of_birth') {
        studentInfo.date_of_birth = value;
      } else if (originalFieldName === 'previous_last_name') {
        studentInfo.previous_last_name = value;
      } else if (originalFieldName === 'social_security_number') {
        studentInfo.social_security_number = value;
      } else if (originalFieldName === 'nickname') {
        studentInfo.nickname = value;
      }
    }

    // Build address from primary household
    if (originalFieldName === 'primary_current_address_street') {
      studentInfo.address_street = formData[prefixedFieldName];
    } else if (originalFieldName === 'primary_current_address_city') {
      studentInfo.address_city = formData[prefixedFieldName];
    } else if (originalFieldName === 'primary_current_address_state') {
      studentInfo.address_state = formData[prefixedFieldName];
    } else if (originalFieldName === 'primary_current_address_zip') {
      studentInfo.address_zip = formData[prefixedFieldName];
    }
  });

  // Combine address
  if (studentInfo.address_street) {
    const addressParts = [
      studentInfo.address_street,
      studentInfo.address_city,
      studentInfo.address_state,
      studentInfo.address_zip
    ].filter(Boolean);
    studentInfo.address = addressParts.join(', ');
  }

  return studentInfo;
};
